import { isMobile } from 'react-device-detect';
import { MeetingViewModel } from '@components';
import {
  AuthService, CalendarRequest, DATE_FORMAT, IMeeting, IMeetingDto,
  IUpdatedValuesWhenDragging, IUser, TCalendarInervalFilter, TSplitedMeetings,
  ceilMeasure, getDateInUserTimezone, getEndInterval, getInterval, getMeetingFromDto,
  getMeetingWithLayer, getStartInterval, getViewCalendar, months, monthsInGenitiveCase,
  shortMonths, IWorkCalendarDay,
} from '@utils';
import { add, differenceInCalendarDays, format, getMonth, sub } from 'date-fns';
import { action, computed, makeObservable, observable, reaction, runInAction } from 'mobx';
import { Inject, Service } from 'typedi';
import { RoomsStore } from '../../store';
import { ProfileViewModel } from '../profilePage';
import { MembersStore } from './../../store/members';
import { IRoom, FilterService, } from '@utils';

@Service()
export class CalendarViewModel {

  @Inject(() => CalendarRequest)
  private calendarRequest: CalendarRequest;

  @Inject(() => FilterService)
  private filterService: FilterService;

  @Inject(() => MeetingViewModel)
  private meetingViewModel: MeetingViewModel;

  @Inject(() => ProfileViewModel)
  private profileViewModel: ProfileViewModel;

  @Inject(() => MembersStore)
  private membersStore: MembersStore;

  @Inject(() => RoomsStore)
  private roomsStore: RoomsStore;

  @observable.shallow dayMeetings: TSplitedMeetings = new Map();

  @observable filterInterval: TCalendarInervalFilter = sessionStorage.getItem('filter') as TCalendarInervalFilter ?? 'month';

  @observable.shallow filteredMeetings: TSplitedMeetings = this.dayMeetings;

  @observable firstDayOfInterval = new Date();

  @observable.shallow viewCalendar: Date[] = getViewCalendar[this.filterInterval](this.firstDayOfInterval);

  @observable startInterval: Date = getInterval.start[this.filterInterval](this.firstDayOfInterval);

  @observable endInterval: Date = getInterval.end[this.filterInterval](this.firstDayOfInterval);

  @observable activeDay = '';

  @observable startHour = 8;

  @observable.ref selectedUser: IUser | null = null;

  @observable.ref selectedRoom: IRoom | null = null;

  @observable isLoading = false;

  @observable today: Date = new Date();

  @observable workCalendar: IWorkCalendarDay[] = [];

  @computed get hours() {
    const hours = [];
    for (let hour = this.startHour; hour < 24; hour++) hours.push(hour);
    return hours;
  }

  @computed get filteredMeetingsValues() {
    let filteredMeetings: IMeeting[] = [];
    for (let meetings of this.filteredMeetings.values())
      filteredMeetings = filteredMeetings.concat(meetings);
    return filteredMeetings;
  }

  @computed get startIntervalOfFilter() {
    return this.filterService.filters.startInterval ?? getStartInterval(this.startInterval, this.filterInterval);
  }

  @computed get endIntervalOfFilter() {
    return this.filterService.filters.endInterval ?? getEndInterval(this.endInterval, this.filterInterval);
  }

  @computed get startMonthInterval() {
    return getStartInterval(this.filterService.filters.startInterval ?? this.startInterval, 'month');
  }

  @computed get endMonthInterval() {
    return getEndInterval(this.filterService.filters.endInterval ?? this.endInterval, 'month');
  }

  @computed get monthRowsBetweenInterval() {
    return (differenceInCalendarDays(this.endIntervalOfFilter, this.startIntervalOfFilter) + 1) / 7;
  }

  @computed get currentDateTitle() {
    const startDate = this.startInterval;
    switch (this.filterInterval) {
      case 'month':
        return `${months[startDate.getMonth()]} ${startDate.getFullYear()}`;
      case 'week':
        return this.getWeekTitle(startDate, this.endInterval);
      default:
        return `${startDate.getDate()} ${monthsInGenitiveCase[startDate.getMonth()].toLocaleLowerCase()} ${startDate.getFullYear()}`;
    }
  }

  @computed get currentMonth() {
    return `${months[this.startInterval.getMonth()]}, ${this.startInterval.getFullYear()}`;
  }

  @action calculateTodayDate = (date: Date) => {
    const offset = this.profileViewModel.minutesOffset;
    this.today = getDateInUserTimezone(date.toUTCString(), offset);
  };

  getWeekTitle = (startDate: Date, endDate: Date) => {
    if (startDate.getMonth() === endDate.getMonth()) {
      return `${startDate.getDate()} — ${endDate.getDate()} ${monthsInGenitiveCase[startDate.getMonth()].toLocaleLowerCase()} ${startDate.getFullYear()}`;
    }
    if (startDate.getFullYear() === endDate.getFullYear()) {
      return `${startDate.getDate()} ${shortMonths[startDate.getMonth()]}. — ${endDate.getDate()} ${shortMonths[endDate.getMonth()]}. ${startDate.getFullYear()}`;
    }
    // если года начала и конца интервала отличаются
    return `${startDate.getDate()} ${shortMonths[startDate.getMonth()]}. ${startDate.getFullYear()} — ${endDate.getDate()} ${shortMonths[endDate.getMonth()]}. ${endDate.getFullYear()}`;
  };

  updateToday = () => {
    const date = new Date();

    if (this.profileViewModel.initPromise) {
      return this.profileViewModel.initPromise.then(() => this.calculateTodayDate(date));
    } else {
      return this.profileViewModel.onInit().then(() => this.calculateTodayDate(date));
    }
  };

  isDayActive = (day: Date) => format(this.today, DATE_FORMAT) === format(day, DATE_FORMAT);

  isCurrentMonth = (day: Date) => getMonth(this.startInterval) === getMonth(day);

  getClassNameForWeekDay = (index: number, isWeekendHoliday = false) =>
    `day weekDay ${format(this.viewCalendar[index], DATE_FORMAT) === format(new Date(), DATE_FORMAT) ? 'today' : ''} ${isWeekendHoliday ? 'weekend-holiday' : ''}`;

  isChoosedDay = (day: Date) =>
    this.filterInterval === 'day' && format(this.viewCalendar[0], DATE_FORMAT) === format(day, DATE_FORMAT);

  isMeetingInOneLine = (timeHeight: number) => timeHeight < 30 / ceilMeasure;

  getTimePoint = (meetingTimePoint: Date) => meetingTimePoint.toLocaleTimeString().slice(0, 5);

  isShortMeeting = (timeHeight: number) => timeHeight <= 45 / ceilMeasure;

  setFilteredMeetingAndStartHour = () => {
    let startHour = 8;
    for (let meetings of this.dayMeetings.entries()) {
      this.filteredMeetings.set(meetings[0], meetings[1].filter((elem) => {
        const isMeetingFiltered = this.filterService.isValidMeeting(elem, this.profileViewModel.minutesOffset);

        isMeetingFiltered && elem.startDate.getHours() < startHour
          && (startHour = elem.startDate.getHours());

        return isMeetingFiltered;
      }));
    }
    this.startHour = startHour;
  };

  findWorkCalendarDay = (day: Date, workCalendar = this.workCalendar) => {
    return workCalendar.find((workDay) => {
      const date = new Date(workDay.date);
      return date.getDate() === day.getDate() && date.getMonth() === day.getMonth();
    });
  };

  subscribeReaction = () => {
    reaction(
      () => this.filterService.selectedItem,
      () => this.updateSelectedUserAndRoom(),
    );

    reaction(
      () => this.filterService.filters, 
      () => {
        this.setFilteredMeetingAndStartHour();
        this.meetingViewModel.setMeetingDraggingId(null);
      }
    );

    reaction(
      () => this.filterService.notCheckedCalendarsIds, 
      () => this.setFilteredMeetingAndStartHour()
    );
    
    reaction(() => this.firstDayOfInterval,
      (date: Date) => {
        this.startInterval = getInterval.start[this.filterInterval](date);
        this.endInterval = getInterval.end[this.filterInterval](date);
        this.viewCalendar = getViewCalendar[this.filterInterval](date);
        this.activeDay = '';
        this.meetingViewModel.setMeetingDraggingId(null);
        this.getWorkCalendar();
      }
    );
  }
  
  constructor() {
    makeObservable(this);
  }

  @action onInit = () => {
    this.subscribeReaction()
    return this.updateToday();
  }

  @action clearCalendarData = () => {
    this.dayMeetings.clear();
    this.filteredMeetings.clear();
  }

  @action setSelectedFilter = (filter: TCalendarInervalFilter) => {
    this.filterInterval = filter;
    sessionStorage.setItem('filter', this.filterInterval);
  };

  @action goToToday = () => {
    this.dayMeetings.clear();
    this.filteredMeetings.clear();
    this.firstDayOfInterval = new Date();
  };

  @action setDay = (day: Date) => {
    this.setSelectedFilter('day');
    this.firstDayOfInterval = day;
  };

  get isAllRoomMeetingView() {
    return this.filterService.showAllRoomsMeetings;
  }

  @action closeAllRoomMeetingView = () => this.filterService.setShowAllRoomsMeetings(false);

  @action getAllMeetings = () => {
    this.activeDay = '';

    const startIntervalDate = this.startIntervalOfFilter.toISOString();
    const endIntervalDate = this.endIntervalOfFilter.toISOString();

    this.isLoading = true;

    if (
      (this.filterService.filters.endInterval && this.startIntervalOfFilter.getTime() > this.filterService.filters.endInterval.getTime())
      || (this.filterService.filters.startInterval && this.endIntervalOfFilter.getTime() < this.filterService.filters.startInterval.getTime())
    ) return;

    const allMeetings = this.selectMeetingsRequest(startIntervalDate, endIntervalDate)

    this.membersStore.initPromise.then(() => {
      allMeetings.then((res) => {
        runInAction(() => {
          this.isLoading = false;
          this.dayMeetings.clear();
          this.filteredMeetings.clear();
          const meetingsFromResponse = res.data as IMeetingDto[];
          meetingsFromResponse.forEach(
            (item) => {
              const meetingAfterDto = getMeetingFromDto(item, this.profileViewModel.minutesOffset, this.filterService.showAllRoomsMeetings);

              const meeting = getMeetingWithLayer(
                0, meetingAfterDto, this.dayMeetings.get(meetingAfterDto.startDate.toDateString())
              );

              this.dayMeetings.set(
                meeting.startDate.toDateString(),
                [...this.dayMeetings.get(meeting.startDate.toDateString()) ?? [], meeting]
              );

            }
          );
          this.setFilteredMeetingAndStartHour();
        });
      });
    });
  };

  @action selectMeetingsRequest = (startIntervalDate: string, endIntervalDate: string) => {
    if (this.filterService.showAllRoomsMeetings) {
      return this.getAllRoomsMeetings(startIntervalDate, endIntervalDate);
    } else {
      return this.selectedRoom
        ? this.getRoomMeetings(startIntervalDate, endIntervalDate)
        : this.getPeopleMeetings(startIntervalDate, endIntervalDate);
    }
  };

  @action getPeopleMeetings = (startIntervalDate: string, endIntervalDate: string) => {
    const currentUserId = this.filterService.selectEmployeeId ?? AuthService.getUserId();

    return this.calendarRequest.getAllMeetings({
      startIntervalDate,
      endIntervalDate,
      userIds: [currentUserId],
      splitByUserId: false,
    });
  };

  @action getRoomMeetings = (startIntervalDate: string, endIntervalDate: string) => {
    const conferenceRoom = this.selectedRoom.conferenceType;

    return this.calendarRequest.getAllMeetings({
      startIntervalDate,
      endIntervalDate,
      conferenceRoom,
      splitByUserId: false,
    });
  };

  @action getAllRoomsMeetings = (startIntervalDate: string, endIntervalDate: string) => {
    return this.calendarRequest.getAllRoomsMeetings({
      startIntervalDate,
      endIntervalDate,
      splitByUserId: false,
      conferenceRooms: 'ALL',
      requestMeeting: 'ALL',
    });
  };

  @action setFirstDayOfIntervalAsCurrent = () => {
    this.firstDayOfInterval = new Date();
  };

  @action movingBeetweenCalendarData = (direction: 'next' | 'prev', filterInterval: TCalendarInervalFilter) => {
    this.dayMeetings.clear();
    this.filteredMeetings.clear();
    const newCalendarData = (direction === 'next' ? add : sub)(this.startInterval, { [filterInterval + 's']: 1 });
    this.firstDayOfInterval = newCalendarData;
  };

  @action setActiveDay = (newValue: string) => this.activeDay = newValue;

  @action setStartHour = (newValue: number) => this.startHour = newValue;

  @action updateSelectedUserAndRoom = () => {
    if (this.filterService.showAllRoomsMeetings) {
      this.selectedUser = null;
      this.selectedRoom = null;
    } else {
      this.selectedUser = this.membersStore.getMemberById(this.filterService.selectEmployeeId) ?? null;
      this.selectedRoom = this.roomsStore.getRoomByCabinetNumber(this.filterService.selectRoomCabinetNumber) ?? null;
      !isMobile && this.getAllMeetings();
    }
  }

  /**
   * Функция, позволяющая перенести встречу из одного дня в другой в Map()
   * @param updatedMeeting встреча с обновленным временем и layer
   * @param oldDay день, на каком встреча была изначальна
   */
  @action moveMeetingToOtherDay = (updatedMeeting: IMeeting, oldDay: Date) => {
    this.filteredMeetings.set(oldDay.toDateString(), (this.filteredMeetings.get(oldDay.toDateString()) ?? []).filter(item => item.id !== updatedMeeting.id));
    this.filteredMeetings.set(updatedMeeting.startDate.toDateString(), [...this.filteredMeetings.get(updatedMeeting.startDate.toDateString()) ?? [], updatedMeeting]);
  };
  /**
   * Функция для обновления или удаления свойства draggedValues у встречи в массиве в filteredMeetings 
   * @param meetingId id встречи
   * @param draggingValues передается либо объект draggingValues (с обновленными значениями) или false. Значение false передается в случаях, если изменения сохранены или отменены. Тогда объект draggedValues удаляется и встреча выставляется в соотвествии с meeting.startDate, meeting.endDate
   * @param day дата, где изначально была встреча
   */
  @action setMeetingDragging = (
    meetingId: number, draggingValues: IUpdatedValuesWhenDragging | false, day: Date
  ) => {
    if (draggingValues) {
      this.meetingViewModel.setMeetingDraggingId(meetingId);
      const meetingsOfDay = (this.filteredMeetings.get(day.toDateString()) ?? []).map(meeting =>
        meeting.id === meetingId && day.getTime() === meeting.startDate.getTime()
          ? { ...meeting, draggingValues }
          : meeting
      );
      this.filteredMeetings.set(day.toDateString(), meetingsOfDay);
      return;
    }

    const meetingsOfDay = (this.filteredMeetings.get(day.toDateString()) ?? []).map(meeting => {
      if (meeting.id !== meetingId && day.getTime() !== meeting.startDate.getTime()) {
        return meeting;
      };
      const updatedMeeting = { ...meeting };
      delete updatedMeeting.draggingValues;
      return updatedMeeting;
    });
    this.filteredMeetings.set(day.toDateString(), meetingsOfDay);
  };

  @action getWorkCalendar = () => (
    this.calendarRequest
      .getWorkCalendar(this.startMonthInterval.toISOString(), this.endMonthInterval.toISOString())
      .then((res) => runInAction(() => this.workCalendar = res.data))
  );

  @action getWorkCalendarFromDateMonth = (date: Date) => {
    return this.calendarRequest.getWorkCalendar(
      getStartInterval(date, 'month').toISOString(), 
      getEndInterval(date, 'month').toISOString(),
    ).then((res) => {
      return res.data;
    });
  }
}
